import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Alert, Spinner, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getSurveys, deleteSurvey } from '../services/api';

const SurveyList = () => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState(null);

  useEffect(() => {
    const fetchSurveys = async () => {
      try {
        setLoading(true);
        const data = await getSurveys();
        setSurveys(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching surveys:', err);
        setError('Failed to load surveys. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSurveys();
  }, []);
  
  const handleOpenDeleteModal = (survey) => {
    setSurveyToDelete(survey);
    setShowDeleteModal(true);
  };
  
  const handleDelete = async () => {
    if (!surveyToDelete) return;
    
    try {
      setDeleteLoading(true);
      await deleteSurvey(surveyToDelete.id);
      // Remove the deleted survey from the state
      setSurveys(surveys.filter(s => s.id !== surveyToDelete.id));
      setShowDeleteModal(false);
      setSurveyToDelete(null);
    } catch (err) {
      console.error('Error deleting survey:', err);
      setError('Failed to delete survey. Please try again later.');
      setShowDeleteModal(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        {error}
      </Alert>
    );
  }

  if (surveys.length === 0) {
    return (
      <div className="text-center my-5">
        <h2>No Surveys Found</h2>
        <p>Get started by creating your first survey!</p>
        <div className="mt-4">
          <Button as={Link} to="/surveys/create" variant="primary" className="mx-2">
            Create Survey
          </Button>
          <Button as={Link} to="/surveys/import" variant="outline-primary" className="mx-2">
            Import from Document
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Surveys</h1>
        <div>
          <Button as={Link} to="/surveys/create" variant="primary" className="mx-2">
            Create Survey
          </Button>
          <Button as={Link} to="/surveys/import" variant="outline-primary">
            Import
          </Button>
        </div>
      </div>
      
      <Row>
        {surveys.map(survey => (
          <Col key={survey.id} lg={4} md={6} className="mb-4">
            <Card className="h-100 survey-card">
              <Card.Body>
                <Card.Title>{survey.title}</Card.Title>
                <Card.Text>
                  {survey.description || 'No description provided'}
                </Card.Text>
                <div>
                  <small className="text-muted">
                    {survey.groups?.length || 0} groups • {
                       (survey.groups?.reduce((total, group) => total + group.questions?.length, 0) || 0) + 
                       (survey.general_questions?.length || 0)
                    } questions
                  </small>
                </div>
              </Card.Body>
              <Card.Footer className="bg-white d-flex justify-content-between">
                <Button 
                  onClick={() => handleOpenDeleteModal(survey)} 
                  variant="outline-danger" 
                  size="sm"
                  className="me-2"
                >
                  <i className="bi bi-trash"></i>
                </Button>
                <Button as={Link} to={`/surveys/${survey.id}`} variant="outline-primary" size="sm" className="me-2">
                  View Details
                </Button>
                <Button as={Link} to={`/surveys/${survey.id}/sessions/create`} variant="primary" size="sm">
                  Start Session
                </Button>
              </Card.Footer>
            </Card>
          </Col>
        ))}
      </Row>
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => !deleteLoading && setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Survey</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete <strong>{surveyToDelete?.title}</strong>?</p>
          <p className="text-danger">This action cannot be undone. All associated sessions, answers, and attachments will be permanently deleted.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleteLoading}>
            {deleteLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              'Delete Survey'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SurveyList;
